/**
 * AM-040 — Frontend read-only access to the tenant's deployment-phase flag.
 *
 * VERIFY AGAINST PROJECT: this file assumes a central `axiosInstance` at
 * `@/api/client` (used by ADR-104 serializationApi.ts and AM-016's api.ts
 * per pishraft.md) and a `useAuthStore` that exposes the current user's
 * tenant context. Confirm both imports resolve before wiring this into any
 * page — do not guess further than what is written here.
 *
 * This intentionally does NOT expose start/end mutations here — those are
 * platform-superadmin-only actions (AM-040 Rule DPM-02) and belong in a
 * separate platform-admin surface, not in a tenant-facing forms page.
 */
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/api/client';

export interface TenantFeatureFlagDTO {
  feature_key: string;
  is_enabled: boolean;
  expires_at: string | null;
  is_active: boolean;
}

const DEPLOYMENT_PHASE_FEATURE_KEY = 'platform.deployment_phase';

async function fetchCurrentTenantFeatureFlags(): Promise<TenantFeatureFlagDTO[]> {
  // VERIFY AGAINST PROJECT: exact path for "my tenant" detail endpoint.
  // TenantDetailSerializer (backend) already nests `feature_flags`.
  const { data } = await axiosInstance.get('/tenants/me/');
  return data.feature_flags ?? [];
}

/**
 * orval-style query hook naming convention (`use...`) per project
 * convention documented in engineering-standards.md.
 */
export function useIsDeploymentPhaseActive() {
  return useQuery({
    queryKey: ['tenant', 'feature-flags', DEPLOYMENT_PHASE_FEATURE_KEY],
    queryFn: fetchCurrentTenantFeatureFlags,
    select: (flags) =>
      flags.find((f) => f.feature_key === DEPLOYMENT_PHASE_FEATURE_KEY)?.is_active ?? false,
    staleTime: 60_000,
  });
}
